import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatINR, formatWeight } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

const STATUS_LABEL = { received: 'Received', in_progress: 'In progress', ready: 'Ready for delivery' };

export default function RepairStatusPage() {
  const { data: rows, isLoading } = useQuery({
    queryKey: ['reports', 'repair-status'],
    queryFn: () => api.get('/reports/repair-status'),
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/repair-status', 'repair-status.csv');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Repair status</h1>
          <p className="mt-1 text-sm text-muted">Every repair not yet delivered, by stage — overdue ones flagged.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      <div className="card table-scroll">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Voucher</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Aasari</th>
              <th className="px-4 py-3">Stage</th>
              <th className="px-4 py-3 text-right">Weight</th>
              <th className="px-4 py-3 text-right">Estimated</th>
              <th className="px-4 py-3">Expected delivery</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !rows?.length ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted">Nothing pending.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.voucher_no} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">{r.voucher_no}</td>
                  <td className="px-4 py-3">{r.customer_name}</td>
                  <td className="px-4 py-3 text-muted">{r.karigarName || '—'}</td>
                  <td className="px-4 py-3">{STATUS_LABEL[r.status] ?? r.status}</td>
                  <td className="px-4 py-3 text-right">{formatWeight(r.total_weight)}</td>
                  <td className="px-4 py-3 text-right">{formatINR(r.estimated_charge)}</td>
                  <td className="px-4 py-3">
                    {r.expected_delivery_date || '—'}
                    {r.isOverdue ? <span className="ml-1.5 font-semibold text-danger">Overdue</span> : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
